import React, { useState, useEffect, forwardRef } from 'react';
import { oidcScopeService, oidcClientScopeMapService } from '../services';
import { Checkbox, Select, Input, SelectOwner, TextareaAutosize } from '../components';
import { getBatchEditValue } from '../helpers';
import { useGlobalState, useMessage } from '../hooks';

const _AddOidcClientForm = (
  { mode, data, submitForm, loading, setLoading, formErrors = {} },
  ref
) => {
  const initialFormState = {
    oidc_client_id: null,
    oidc_client_name: '',
    oidc_client_description: '',
    oidc_client_is_enabled: true,
    owner_principal_id: null,
  };

  const initialBatchEditItems = {
    batch_oidc_client_is_enabled: null,
    batch_scope_map: false,
    batch_owner_principal_id: null,
  };

  const [loggedInUser] = useGlobalState('loggedInUser');
  const [formData, setFormData] = useState(initialFormState);

  const [clientScopeMap, setClientScopeMap] = useState([]);
  const [clientScopeMaps, setClientScopeMaps] = useState({});
  const [formClientScopeMap, setFormClientScopeMap] = useState([]);
  const [formClientScopeMapDelta, setFormClientScopeMapDelta] = useState({});

  const [displayNames] = useGlobalState('displayNames');
  const [batchEdit, setBatchEdit] = useState(false);
  const [batchEditItems, setBatchEditItems] = useState(initialBatchEditItems);

  const { showMessage, hideMessage } = useMessage();

  useEffect(() => {
    // Get Scopes
    fetchScopes();
  }, []);

  useEffect(() => {
    setBatchEdit(false);
    if (data.length === 1) {
      setFormData({
        oidc_client_id: data[0].oidc_client_id,
        oidc_client_name: data[0].oidc_client_name || '',
        oidc_client_description: data[0].oidc_client_description || '',
        oidc_client_is_enabled: data[0].oidc_client_is_enabled,
        owner_principal_id: data[0].owner_principal_id,
      });
    } else if (data.length > 1 && mode !== 'add') {
      setBatchEdit(true);

      setFormData({
        oidc_client_is_enabled: getBatchEditValue(data, 'oidc_client_is_enabled'),
        owner_principal_id: getBatchEditValue(data, 'owner_principal_id'),
      });
    } else if (mode === 'add') {
      setFormData({
        ...formData,
        owner_principal_id: loggedInUser?.principal_id,
      });
    } else {
      resetForm();
    }
  }, [mode, data]);

  const fetchScopes = async () => {
    oidcScopeService
      .getAll()
      .then(res => {
        const tmp = res.oidc_scopes.map(x => ({
          value: x.oidc_scope_id,
          label: x.oidc_scope_name,
          secondaryLabel: x.oidc_scope_description,
        }));
        setClientScopeMap(tmp);

        fetchClientScopeMap(tmp);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchClientScopeMap = async SCOPES => {
    const clientIds = data.map(x => x.oidc_client_id).join(',');

    if (clientIds.length > 0) {
      oidcClientScopeMapService
        .getByClientId(clientIds)
        .then(res => {
          const result = res?.oidc_client_scope_map;
          if (result) {
            // Batch edit
            if (data.length > 1 && mode !== 'add') {
              // Build display value with partial items
              let displayValue = [];
              SCOPES.forEach(scope => {
                const items = result.filter(x => scope.value === x.oidc_scope_id);
                if (items.length === data.length) {
                  displayValue.push({ value: scope.value });
                } else if (items.length > 0) {
                  displayValue.push({ value: scope.value, partial: true });
                }
              });
              setFormClientScopeMap(displayValue);

              // Save per-client individual maps as arrays for use at submit time
              let tmp = {};
              result.forEach(x => {
                const prev = tmp[x.oidc_client_id] || [];
                tmp[x.oidc_client_id] = [...prev, x.oidc_scope_id];
              });
              setClientScopeMaps(tmp);

              // Add or single edit
            } else {
              setFormClientScopeMap(result.map(x => ({ value: x.oidc_scope_id })));
            }
          }
        })
        .catch(err => {
          showMessage(err, 'error');
        });
    }
  };

  const handleClientScopeMapChange = ({ value: val }) => {
    setFormClientScopeMap(prev => {
      const exists = prev.find(x => x.value === val);
      if (exists) {
        if (batchEdit) setFormClientScopeMapDelta(prev => ({ ...prev, [val]: false }));
        return prev.filter(x => x.value !== val);
      } else {
        if (batchEdit) setFormClientScopeMapDelta(prev => ({ ...prev, [val]: true }));
        return [...prev, { value: val }];
      }
    });
  };

  const handleInputChange = async (event, formPart) => {
    let { name, value } = '';

    // Handle standard form inputs
    if (event.target !== undefined) {
      name = event.target.name;
      value = event.target.value;

      // If input element is a checkbox, we cannot use "value"
      if (event.target.type === 'checkbox') {
        value = event.target.checked;
      }

      // Handle custom form inputs
    } else {
      name = event.name;
      value = event.value;
    }

    switch (formPart) {
      case 'batch':
        setBatchEditItems({ ...batchEditItems, [name]: value });
        break;
      default:
        setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Hide errors on input change
    if (formErrors && formErrors[name]) {
      delete formErrors[name];
      hideMessage();
    }
  };

  const onSubmit = event => {
    event.preventDefault();

    setLoading(true);

    // Form validation
    //if (!formData.user_fullname || !formData.user_username) return

    let dataToSubmit;

    if (batchEdit) {
      let key = '';
      let tmp = {};
      const objects = Object.entries(batchEditItems);

      for (let i = 0; i < objects.length; i++) {
        if (objects[i][1]) {
          key = objects[i][0].replace('batch_', '');
          tmp = { ...tmp, [key]: formData[key] };
        }
      }

      dataToSubmit = data.map(item => {
        // Restore the individual scope map for this client and apply the delta
        const individualMap = clientScopeMaps[item.oidc_client_id] || [];
        let clientScopes = [...individualMap];
        Object.entries(formClientScopeMapDelta).forEach(([scopeId, added]) => {
          if (added && !clientScopes.includes(scopeId)) clientScopes.push(scopeId);
          else if (!added) clientScopes = clientScopes.filter(x => x !== scopeId);
        });

        return {
          oidc_client_id: item.oidc_client_id,
          oidc_client_name: item.oidc_client_name,
          oidc_client_description: item.oidc_client_description,
          owner_principal_id: item.owner_principal_id,
          ...tmp,
          clientScopeMap: clientScopes,
        };
      });
    } else {
      dataToSubmit = {
        ...formData,
        clientScopeMap: formClientScopeMap.map(x => x.value),
      };
    }

    // submit form
    submitForm(dataToSubmit)
      .then(() => resetForm())
      .catch(() => {
        // Errors handled in submitForm function
      })
      .finally(() => setLoading(false));
  };

  const resetForm = () => {
    setBatchEdit(false);
    setBatchEditItems(initialBatchEditItems);
    setFormClientScopeMap([]);
    setFormClientScopeMapDelta({});
    setFormData(() => {
      return {
        ...initialFormState,
        owner_principal_id: loggedInUser ? loggedInUser.principal_id : null,
      };
    });
  };

  return (
    <div className="form">
      <form onSubmit={onSubmit} ref={ref}>
        {!batchEdit && (
          <div className="form-block vertical">
            <Input
              type="text"
              name="oidc_client_name"
              label={displayNames?.oidc_client_name.display_name}
              value={formData.oidc_client_name}
              title={displayNames?.oidc_client_name.display_name_description}
              onChange={handleInputChange}
              disabled={loading}
              validation={
                formErrors.oidc_client_name
                  ? { valid: false, message: formErrors.oidc_client_name }
                  : undefined
              }
            />
          </div>
        )}
        {!batchEdit && (
          <div className="form-block vertical">
            <TextareaAutosize
              label={displayNames?.oidc_client_description.display_name}
              name="oidc_client_description"
              title={displayNames?.oidc_client_description.display_name_description}
              value={formData.oidc_client_description}
              onChange={handleInputChange}
              disabled={loading}
              isOptional={true}
            />
          </div>
        )}

        <div className="form-block vertical">
          {batchEdit && (
            <label>
              <Checkbox
                checked={batchEditItems.batch_owner_principal_id}
                name="batch_owner_principal_id"
                label="Edit Owner"
                onChange={e => handleInputChange(e, 'batch')}
                disabled={loading}
              />
            </label>
          )}

          <SelectOwner
            name="owner_principal_id"
            value={
              formData.owner_principal_id === 'multiple' ? undefined : formData.owner_principal_id
            }
            label={!batchEdit && 'Owner'}
            placeholder={formData.owner_principal_id === 'multiple' ? '< Multiple >' : ''}
            onChange={handleInputChange}
            disabled={loading || (batchEdit && !batchEditItems.batch_owner_principal_id)}
          />
        </div>

        <div className="form-block vertical">
          {batchEdit && (
            <label>
              <Checkbox
                checked={batchEditItems.batch_oidc_scope_map}
                name="batch_oidc_scope_map"
                label="Edit Scopes"
                onChange={e => handleInputChange(e, 'batch')}
                disabled={loading}
              />
            </label>
          )}
          <Select
            name="oidc_client_scope_map"
            value={formClientScopeMap}
            options={clientScopeMap}
            onChange={handleClientScopeMapChange}
            label={!batchEdit && displayNames?.oidc_client_scope_map?.display_name}
            title={displayNames?.oidc_client_scope_map?.display_name_description}
            isMulti={true}
            disabled={loading || (batchEdit && !batchEditItems.batch_oidc_scope_map)}
            sortOptions={false}
          />
        </div>

        <div className="form-block vertical">
          {batchEdit ? (
            <label>
              <Checkbox
                checked={batchEditItems.batch_oidc_client_is_enabled}
                name="batch_oidc_client_is_enabled"
                label="Edit Status"
                onChange={e => handleInputChange(e, 'batch')}
                disabled={loading}
              />
            </label>
          ) : null}

          <div className="horizontal">
            <Checkbox
              name="oidc_client_is_enabled"
              checked={formData.oidc_client_is_enabled}
              onChange={handleInputChange}
              label={displayNames?.oidc_client_is_enabled.display_name}
              title={displayNames?.oidc_client_is_enabled.display_name_description}
              disabled={loading || (batchEdit && !batchEditItems.batch_oidc_client_is_enabled)}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export const AddOidcClientForm = forwardRef(_AddOidcClientForm);
